import { z } from 'zod';

import type { OpeningSlot } from './monitor';
import { addDays, mondayOf } from './play-report';
import { lisbonHour, lisbonMidnight } from './time';

/**
 * Occupancy of a room and of each PC over a day, week, month or year (RF-D3),
 * for the back-office (Arena > Ocupação). Monitors of the arena and admins
 * only (RN-D1).
 *
 * Rules:
 * - a session counts from its start to its end, or to its last sign of life
 *   while still open; a session across midnight is split between the days;
 * - occupancy = PC minutes in use inside the opening hours ÷ PC minutes the
 *   room was open (PCs that existed at the time × opening hours). Without
 *   opening hours there is no percentage, only hours;
 * - the current period counts only up to `now`, so today is not diluted by
 *   the hours still to come;
 * - youth and event sessions are counted apart (RF-C2); both occupy the PC.
 *
 * Only totals leave here: no logins, no account ids (RN-A16).
 */

export const occupancyPeriodSchema = z.enum(['day', 'week', 'month', 'year']);
export type OccupancyPeriod = z.infer<typeof occupancyPeriodSchema>;

/** The Lisbon dates (inclusive) of the period that holds `date`. */
export function occupancyRange(
  period: OccupancyPeriod,
  date: string,
): { from: string; to: string } {
  switch (period) {
    case 'day':
      return { from: date, to: date };
    case 'week': {
      const from = mondayOf(date);
      return { from, to: addDays(from, 6) };
    }
    case 'month': {
      const from = `${date.slice(0, 7)}-01`;
      return { from, to: addDays(nextMonth(from), -1) };
    }
    case 'year':
      return { from: `${date.slice(0, 4)}-01-01`, to: `${date.slice(0, 4)}-12-31` };
  }
}

/** A date inside the period before (`-1`) or after (`1`) the one that holds `date`. */
export function shiftOccupancyDate(period: OccupancyPeriod, date: string, step: -1 | 1): string {
  const { from, to } = occupancyRange(period, date);
  return step === 1 ? addDays(to, 1) : occupancyRange(period, addDays(from, -1)).from;
}

function nextMonth(firstOfMonth: string): string {
  const [y, m] = firstOfMonth.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
}

export interface OccupancySession {
  workstationId: string;
  /** Null for an event session. */
  accountId: string | null;
  start: Date;
  /** End, or last sign of life while open. */
  end: Date;
}

export interface OccupancyWorkstation {
  id: string;
  name: string;
  createdAt: Date;
  retiredAt: Date | null;
}

export interface OccupancyInput {
  period: OccupancyPeriod;
  /** Any Lisbon date inside the period. */
  date: string;
  sessions: readonly OccupancySession[];
  workstations: readonly OccupancyWorkstation[];
  openingHours: readonly OpeningSlot[];
  now: Date;
}

const statsSchema = z.object({
  youthMinutes: z.number(),
  eventMinutes: z.number(),
  /** Sessions that started in the period (or bucket). */
  sessions: z.number(),
  /** PC minutes the room was open. 0 without opening hours. */
  openPcMinutes: z.number(),
  /** PC minutes in use inside the opening hours. */
  usedOpenMinutes: z.number(),
  /** 0..1, or null when there were no open hours to compare with. */
  occupancy: z.number().nullable(),
});
export type OccupancyStats = z.infer<typeof statsSchema>;

export const occupancyReportSchema = z.object({
  period: occupancyPeriodSchema,
  from: z.string(),
  to: z.string(),
  hasOpeningHours: z.boolean(),
  totals: statsSchema.extend({
    /** How many different youths played: a count, never who. */
    distinctYouths: z.number(),
    workstations: z.number(),
  }),
  /** Hours of the day (`"0"`..`"23"`), dates, or months (`YYYY-MM`) for a year. */
  buckets: z.array(statsSchema.extend({ key: z.string() })),
  /** Average PCs in use per weekday (1 = Monday) and hour; empty for a day. */
  heatmap: z.array(
    z.object({
      weekday: z.number(),
      hour: z.number(),
      busyPcs: z.number(),
      open: z.boolean(),
    }),
  ),
  workstations: z.array(
    statsSchema.extend({ id: z.string(), name: z.string(), retired: z.boolean() }),
  ),
});
export type OccupancyReport = z.infer<typeof occupancyReportSchema>;

export const occupancyQuerySchema = z.object({
  period: occupancyPeriodSchema.default('week'),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

const HOUR = 3_600_000;
const MINUTE = 60_000;

interface HourCell {
  start: number;
  date: string;
  hour: number;
  weekday: number;
  bucket: number;
  /** Minutes of this hour already past at `now` (0..60). */
  elapsed: number;
}

interface Acc {
  youth: number;
  event: number;
  sessions: number;
  openPc: number;
  usedOpen: number;
}

const emptyAcc = (): Acc => ({ youth: 0, event: 0, sessions: 0, openPc: 0, usedOpen: 0 });

function weekdayOf(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return ((new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7) + 1;
}

const minutesOf = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/** Wall-clock minutes of `[from, to)` (minutes of the day) inside the opening slots. */
function openOverlap(
  slots: readonly { opens: number; closes: number }[],
  from: number,
  to: number,
) {
  let total = 0;
  for (const s of slots) total += Math.max(0, Math.min(to, s.closes) - Math.max(from, s.opens));
  return total;
}

function bucketKey(period: OccupancyPeriod, date: string, hour: number): string {
  if (period === 'day') return String(hour);
  if (period === 'year') return date.slice(0, 7);
  return date;
}

function toStats(a: Acc): OccupancyStats {
  const r = (n: number) => Math.round(n * 10) / 10;
  return {
    youthMinutes: r(a.youth),
    eventMinutes: r(a.event),
    sessions: a.sessions,
    openPcMinutes: r(a.openPc),
    usedOpenMinutes: r(a.usedOpen),
    occupancy: a.openPc > 0 ? Math.min(1, a.usedOpen / a.openPc) : null,
  };
}

export function computeOccupancy(input: OccupancyInput): OccupancyReport {
  const { period, now } = input;
  const { from, to } = occupancyRange(period, input.date);
  const periodStart = lisbonMidnight(from).getTime();
  const periodEnd = lisbonMidnight(addDays(to, 1)).getTime();
  const nowMs = now.getTime();

  const slotsByWeekday = new Map<number, { opens: number; closes: number }[]>();
  for (const s of input.openingHours) {
    const list = slotsByWeekday.get(s.weekday) ?? [];
    list.push({ opens: minutesOf(s.opens), closes: minutesOf(s.closes) });
    slotsByWeekday.set(s.weekday, list);
  }

  // Every hour of the period. Lisbon is a whole number of hours off UTC, so
  // UTC hours are Lisbon hours, including on the days the clocks change.
  const bucketIndex = new Map<string, number>();
  const hours: HourCell[] = [];
  for (let t = periodStart; t < periodEnd; t += HOUR) {
    const { date, hour } = lisbonHour(new Date(t));
    const key = bucketKey(period, date, hour);
    if (!bucketIndex.has(key)) bucketIndex.set(key, bucketIndex.size);
    hours.push({
      start: t,
      date,
      hour,
      weekday: weekdayOf(date),
      bucket: bucketIndex.get(key)!,
      elapsed: Math.max(0, Math.min(60, (nowMs - t) / MINUTE)),
    });
  }
  const openIn = (cell: HourCell, fromMin: number, toMin: number) =>
    openOverlap(
      slotsByWeekday.get(cell.weekday) ?? [],
      cell.hour * 60 + fromMin,
      cell.hour * 60 + toMin,
    );

  // Only PCs that existed at some point of the period (until now).
  const seen = Math.min(periodEnd, nowMs);
  const workstations = input.workstations.filter(
    (w) =>
      w.createdAt.getTime() < seen && (w.retiredAt === null || w.retiredAt.getTime() > periodStart),
  );
  const used = new Set(
    input.sessions
      .filter((s) => s.start.getTime() < periodEnd && s.end.getTime() > periodStart)
      .map((s) => s.workstationId),
  );
  const listed = [
    ...workstations,
    ...input.workstations.filter((w) => used.has(w.id) && !workstations.includes(w)),
  ];

  const buckets = [...bucketIndex.keys()].map(emptyAcc);
  const perPc = new Map(listed.map((w) => [w.id, emptyAcc()]));
  const totals = emptyAcc();
  const heat = new Map<string, { used: number; elapsed: number; open: boolean }>();
  const heatCell = (weekday: number, hour: number) => {
    const k = `${String(weekday)}-${String(hour)}`;
    let cell = heat.get(k);
    if (!cell) {
      cell = { used: 0, elapsed: 0, open: false };
      heat.set(k, cell);
    }
    return cell;
  };

  // What the room could offer: PCs that existed in each hour × open minutes.
  for (const cell of hours) {
    const h = heatCell(cell.weekday, cell.hour);
    h.open ||= openIn(cell, 0, 60) > 0;
    if (cell.elapsed <= 0) continue;
    h.elapsed += cell.elapsed;
    const open = openIn(cell, 0, cell.elapsed);
    if (open <= 0) continue;
    const cellEnd = cell.start + cell.elapsed * MINUTE;
    for (const w of workstations) {
      if (w.createdAt.getTime() >= cellEnd) continue;
      if (w.retiredAt !== null && w.retiredAt.getTime() <= cell.start) continue;
      perPc.get(w.id)!.openPc += open;
      buckets[cell.bucket]!.openPc += open;
      totals.openPc += open;
    }
  }

  // What was used.
  const youths = new Set<string>();
  const limit = Math.min(periodEnd, nowMs);
  for (const s of input.sessions) {
    const pc = perPc.get(s.workstationId);
    const start = Math.max(s.start.getTime(), periodStart);
    const end = Math.min(s.end.getTime(), limit);
    const startedHere = s.start.getTime() >= periodStart && s.start.getTime() < periodEnd;
    if (startedHere) {
      const idx = Math.floor((s.start.getTime() - periodStart) / HOUR);
      buckets[hours[idx]!.bucket]!.sessions += 1;
      totals.sessions += 1;
      if (pc) pc.sessions += 1;
    }
    if (end <= start) continue;
    if (s.accountId !== null) youths.add(s.accountId);
    const kind = s.accountId === null ? 'event' : 'youth';
    for (let idx = Math.floor((start - periodStart) / HOUR); idx < hours.length; idx += 1) {
      const cell = hours[idx]!;
      if (cell.start >= end) break;
      const a = (Math.max(start, cell.start) - cell.start) / MINUTE;
      const b = (Math.min(end, cell.start + HOUR) - cell.start) / MINUTE;
      const minutes = b - a;
      if (minutes <= 0) continue;
      const open = openIn(cell, a, b);
      for (const acc of [buckets[cell.bucket]!, totals, pc]) {
        if (!acc) continue;
        acc[kind] += minutes;
        acc.usedOpen += open;
      }
      heatCell(cell.weekday, cell.hour).used += minutes;
    }
  }

  const heatmap =
    period === 'day'
      ? []
      : [...heat]
          .map(([k, v]) => {
            const [weekday, hour] = k.split('-').map(Number) as [number, number];
            return {
              weekday,
              hour,
              busyPcs: v.elapsed > 0 ? Math.round((v.used / v.elapsed) * 100) / 100 : 0,
              open: v.open,
            };
          })
          .sort((x, y) => x.weekday - y.weekday || x.hour - y.hour);

  return {
    period,
    from,
    to,
    hasOpeningHours: input.openingHours.length > 0,
    totals: { ...toStats(totals), distinctYouths: youths.size, workstations: workstations.length },
    buckets: [...bucketIndex.keys()].map((key, i) => ({ key, ...toStats(buckets[i]!) })),
    heatmap,
    workstations: listed
      .map((w) => ({
        id: w.id,
        name: w.name,
        retired: w.retiredAt !== null && w.retiredAt.getTime() <= nowMs,
        ...toStats(perPc.get(w.id)!),
      }))
      .sort((x, y) => x.name.localeCompare(y.name, 'pt', { numeric: true })),
  };
}
